import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, RefreshCw, CheckCircle2, AlertTriangle, Building2, CalendarClock } from "lucide-react";
import { MONTHS, CURRENT_YEAR, YEAR_OPTIONS, fmtDays } from "@/pages/hr/leave/leaveUtils";

export default function LeaveAutoCountTab({ reloadTypes }) {
  const [status, setStatus] = useState(null);
  const [batches, setBatches] = useState([]);
  const loadBatches = () => api.get("/hr/leave/import-batches").then((r) => setBatches(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/hr/autocount/status").then((r) => setStatus(r.data)).catch((e) => setStatus({ error: formatApiError(e.response?.data?.detail) }));
    loadBatches();
  }, []);

  return (
    <div data-testid="leave-autocount-tab" className="space-y-5">
      <div className="rounded-lg border border-slate-200 bg-white p-4 flex items-center gap-6 text-sm" data-testid="leave-ac-status">
        {status?.error ? <span className="text-rose-600 flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{status.error}</span>
          : status ? (<>
            <span className="flex items-center gap-2">{status.connected ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-rose-600" />}<b>{status.connected ? "Connected" : "Not connected"}</b></span>
            {status.company && <span className="flex items-center gap-1.5 text-slate-600"><Building2 className="w-4 h-4" />{status.company.name} ({status.company.reg})</span>}
            <span className="ml-auto text-xs text-slate-400">AutoCount is read-only</span>
          </>) : <span className="text-slate-400">Checking AutoCount…</span>}
      </div>

      <TypesSection disabled={!status?.connected} onDone={() => { reloadTypes?.(); }} />
      <BalancesSection disabled={!status?.connected} onDone={loadBatches} />
      <HistoricalSection disabled={!status?.connected} onDone={loadBatches} />

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="leave-import-batches">
        <div className="px-4 py-2.5 border-b border-slate-100 text-sm font-semibold">Import History</div>
        {batches.length === 0 ? <div className="px-4 py-5 text-center text-sm text-slate-400">No imports yet</div>
          : batches.map((b) => (
            <div key={b.id} className="flex items-center justify-between px-4 py-2 border-b border-slate-50 last:border-0 text-xs" data-testid={`batch-${b.id}`}>
              <span><Badge variant="outline" className="mr-2">{b.kind}</Badge>
                {b.kind === "balance" ? `As of ${b.as_of_date}${b.set_as_opening ? " · Opening cutover" : " · Snapshot"}` : `Years ${(b.years || []).join(", ")}`}</span>
              <span className="text-slate-500">
                {b.kind === "balance" ? `${b.snapshot_rows} rows · ${b.opening_posted} opening` : `${b.imported} imported · ${b.skipped_duplicates} dup`}
                {" · "}{(b.imported_at || "").slice(0, 10)} · {b.imported_by}</span>
            </div>
          ))}
      </div>
    </div>
  );
}

const Panel = ({ title, children }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4">
    <h3 className="font-semibold text-sm mb-3">{title}</h3>{children}
  </div>
);

function TypesSection({ disabled, onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const doPreview = async () => { setBusy(true); try { const { data } = await api.post("/hr/leave/autocount/types/preview"); setPreview(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } setBusy(false); };
  const doImport = async () => { setBusy(true); try { const { data } = await api.post("/hr/leave/autocount/types/import"); toast.success(`${data.created_count} new leave type(s) imported`); onDone?.(); doPreview(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } setBusy(false); };
  return (
    <Panel title="1. Leave Types">
      <div className="flex gap-2 mb-2">
        <Button size="sm" variant="outline" onClick={doPreview} disabled={disabled || busy} data-testid="ac-types-preview"><RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />Preview</Button>
        {preview && <Button size="sm" className="bg-slate-900" onClick={doImport} disabled={busy} data-testid="ac-types-import"><Download className="w-4 h-4 mr-1.5" />Import New</Button>}
      </div>
      {preview && (
        <div className="text-sm">
          <div className="flex gap-2 mb-2"><Badge variant="outline" className="text-emerald-700 border-emerald-300">New: {preview.summary.new}</Badge><Badge variant="outline" className="text-sky-700 border-sky-300">Existing: {preview.summary.existing}</Badge></div>
          <div className="flex flex-wrap gap-1.5" data-testid="ac-types-rows">
            {preview.rows.map((r) => <Badge key={r.autocount_code} variant="outline" className={r.match_status === "new" ? "text-emerald-700 border-emerald-300" : "text-sky-700 border-sky-300"}>{r.autocount_code} · {r.name}</Badge>)}
          </div>
        </div>
      )}
    </Panel>
  );
}

function BalancesSection({ disabled, onDone }) {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [setOpening, setSetOpening] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const doPreview = async () => { setBusy(true); try { const { data } = await api.post("/hr/leave/autocount/balances/preview", { year, month }); setPreview(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } setBusy(false); };
  const doImport = async () => {
    const label = `${MONTHS[month - 1]} ${year}`;
    if (!window.confirm(`Import AutoCount leave balances as of ${label}?${setOpening ? "\n\nThis will SET the ERP opening balance (cutover). Existing opening balances are preserved, not overwritten." : ""}`)) return;
    setBusy(true);
    try { const { data } = await api.post("/hr/leave/autocount/balances/import", { year, month, set_as_opening: setOpening });
      toast.success(`${data.snapshot_rows} snapshot rows · ${data.opening_posted} opening posted${data.opening_skipped_existing ? ` · ${data.opening_skipped_existing} preserved` : ""}`);
      onDone?.(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } setBusy(false);
  };
  return (
    <Panel title="2. Opening Balance (snapshot at cutover)">
      <div className="flex flex-wrap gap-3 items-end mb-3">
        <div><Label className="text-xs">As-of Year</Label>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}><SelectTrigger className="h-8 w-28" data-testid="ac-bal-year"><SelectValue /></SelectTrigger>
            <SelectContent>{YEAR_OPTIONS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-xs">As-of Month</Label>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}><SelectTrigger className="h-8 w-28" data-testid="ac-bal-month"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent></Select></div>
        <label className="flex items-center gap-2 text-sm mb-1"><Checkbox checked={setOpening} onCheckedChange={(v) => setSetOpening(!!v)} data-testid="ac-bal-set-opening" />Set as ERP opening balance (cutover)</label>
        <Button size="sm" variant="outline" onClick={doPreview} disabled={disabled || busy} data-testid="ac-bal-preview"><RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />Preview</Button>
      </div>
      {preview && (
        <div className="text-sm">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-2 mb-2 flex items-center gap-2 text-xs" data-testid="ac-bal-period">
            <CalendarClock className="w-4 h-4 text-sky-600" />Opening Balance As Of: <b>{MONTHS[month - 1]} {year}</b>
            <span className="ml-auto flex gap-2"><Badge variant="outline" className="text-emerald-700 border-emerald-300">Matched: {preview.summary.matched}</Badge>
              {preview.summary.unmatched_employee_codes?.length > 0 && <Badge variant="outline" className="text-rose-700 border-rose-300">Unmatched: {preview.summary.unmatched_employee_codes.length}</Badge>}</span>
          </div>
          <Button size="sm" className="bg-slate-900 mb-2" onClick={doImport} disabled={busy} data-testid="ac-bal-import"><Download className="w-4 h-4 mr-1.5" />Confirm Import</Button>
          <div className="max-h-56 overflow-y-auto border border-slate-100 rounded-md">
            <table className="excel-table w-full"><thead><tr><th>Employee</th><th>Type</th><th>Entitled</th><th>Taken</th><th>Balance</th></tr></thead>
              <tbody>{preview.rows.slice(0, 200).map((r, i) => (
                <tr key={i} className={r.matched ? "" : "opacity-50"}>
                  <td className="text-xs">{r.matched_fas_id || r.autocount_employee_code}</td>
                  <td className="text-xs">{r.leave_type_code}</td>
                  <td className="text-center">{fmtDays(r.entitlement)}</td>
                  <td className="text-center">{fmtDays(r.taken)}</td>
                  <td className="text-center font-semibold">{fmtDays(r.balance)}</td>
                </tr>))}</tbody></table>
          </div>
        </div>
      )}
    </Panel>
  );
}

function HistoricalSection({ disabled, onDone }) {
  const [years, setYears] = useState([CURRENT_YEAR]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const toggle = (y) => setYears((p) => (p.includes(y) ? p.filter((x) => x !== y) : [...p, y]));
  const doPreview = async () => { if (!years.length) return toast.error("Select at least one year"); setBusy(true); try { const { data } = await api.post("/hr/leave/autocount/historical/preview", { years }); setPreview(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } setBusy(false); };
  const doImport = async () => {
    if (!window.confirm(`Import historical leave transactions for ${years.sort().join(", ")}? (reference-only, no balance impact)`)) return;
    setBusy(true);
    try { const { data } = await api.post("/hr/leave/autocount/historical/import", { years });
      toast.success(`${data.imported} imported · ${data.skipped_duplicates} duplicate(s) skipped`); onDone?.(); doPreview(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } setBusy(false);
  };
  return (
    <Panel title="3. Historical Leave Transactions (reference only)">
      <div className="flex flex-wrap gap-3 items-center mb-3">
        <span className="text-xs text-slate-500">Year(s):</span>
        {YEAR_OPTIONS.map((y) => (
          <label key={y} className="flex items-center gap-1.5 text-sm"><Checkbox checked={years.includes(y)} onCheckedChange={() => toggle(y)} data-testid={`ac-hist-year-${y}`} />{y}</label>
        ))}
        <Button size="sm" variant="outline" onClick={doPreview} disabled={disabled || busy} data-testid="ac-hist-preview"><RefreshCw className={`w-4 h-4 mr-1.5 ${busy ? "animate-spin" : ""}`} />Preview</Button>
      </div>
      {preview && (
        <div className="text-sm">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-2 mb-2 text-xs flex items-center gap-2" data-testid="ac-hist-period">
            Historical Transactions: <b>{preview.years.join(", ")}</b>
            <span className="ml-auto flex gap-2"><Badge variant="outline">Records: {preview.summary.records}</Badge>
              <Badge variant="outline" className="text-emerald-700 border-emerald-300">New: {preview.summary.new}</Badge>
              <Badge variant="outline" className="text-sky-700 border-sky-300">Already imported: {preview.summary.already_imported}</Badge></span>
          </div>
          <Button size="sm" className="bg-slate-900" onClick={doImport} disabled={busy} data-testid="ac-hist-import"><Download className="w-4 h-4 mr-1.5" />Confirm Import</Button>
          {preview.summary.unmatched_employee_codes?.length > 0 && <p className="text-xs text-rose-600 mt-2">Unmatched codes: {preview.summary.unmatched_employee_codes.join(", ")}</p>}
        </div>
      )}
    </Panel>
  );
}
